"use client";

import React from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const pricingFactors = [{
  tag: "CONDITION",
  title: "New vs. Pre-Owned",
  desc: "Factory-assembled new builds versus inspected pre-owned equipment with varying histories."
}, {
  tag: "CONFIG",
  title: "Included Equipment",
  desc: "Specific landing arena dimensions, commercial air blowers, and accessory packages."
}, {
  tag: "CONTROL",
  title: "Console & Safety Systems",
  desc: "Operator console options, speed controls, and integrated safety shut-off mechanisms."
}, {
  tag: "FREIGHT",
  title: "Logistics & Delivery",
  desc: "Commercial crating, liftgate requirements, and destination shipping parameters."
}];
export default function Hero() {
  return <section id="hero" data-nav="light" className="relative bg-background text-foreground border-b border-border overflow-hidden">
      {/* Precision Industrial Architectural Grid Accents */}
      <div className="absolute inset-0 pointer-events-none opacity-40">
        <div className="mx-auto max-w-7xl h-full border-x border-border/60" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8 lg:px-12 pt-16 pb-20 md:pt-24 md:pb-28">
        {/* Datum Line Header Registration */}
        <div className="flex items-center gap-2.5 text-muted-foreground font-mono text-xs md:text-sm tracking-wider uppercase mb-6">
          <span className="inline-block w-2.5 h-2.5 bg-primary" />
          <span className="font-semibold text-foreground">MECHANICAL BULL BUYING COSTS</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Main Title and Direct Context */}
          <div className="lg:col-span-7">
            <h1 className="font-serif text-4xl sm:text-6xl lg:text-7xl font-bold tracking-tight uppercase leading-[0.98] text-foreground mb-6">
              Mechanical Bull Price & Cost Guide
            </h1>

            <p className="font-sans text-lg sm:text-xl text-muted-foreground leading-relaxed max-w-2xl mb-8">
              How much does a mechanical bull cost? There is no single useful price that applies to every machine. The
              final cost depends on whether the bull is new or used, the equipment included with it, its condition,
              controller, safety arena, accessories, shipping requirements and other product-specific factors. Mechanical
              Bull Shop uses current inventory and visible product pricing wherever possible so buyers can compare
              actual machines instead of relying on generic industry estimates.
            </p>

            {/* Primary Action Row */}
            <div className="flex flex-wrap items-center gap-4 pt-2">
              <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-3.5 font-serif uppercase tracking-wider text-base font-semibold transition-colors duration-150">
                <Link to="#current-inventory-prices">
                  VIEW CURRENT PRICES
                  <ArrowRight className="size-4 ml-2" />
                </Link>
              </Button>

              <Button asChild variant="outline" className="border-foreground text-foreground hover:bg-foreground hover:text-background px-6 py-3.5 font-serif uppercase tracking-wider text-base font-semibold transition-colors duration-150">
                <Link to="/NewMechanicalBullsPage">
                  BROWSE MECHANICAL BULLS
                </Link>
              </Button>
            </div>
          </div>

          {/* Right Column: Industrial Spec Card Panel */}
          <div className="lg:col-span-5">
            <div className="border border-border bg-card p-6 md:p-7 shadow-xs">
              <div className="flex items-center justify-between pb-4 border-b border-border">
                <span className="font-serif font-bold uppercase tracking-wider text-lg">
                  Key Cost Variables
                </span>
                <span className="font-mono text-xs text-muted-foreground border border-border px-2 py-0.5 bg-muted">
                  EVALUATION CRITERIA
                </span>
              </div>

              <div className="mt-5 space-y-3">
                {pricingFactors.map((factor, i) => <div key={factor.title} data-index={i} className="p-4 border border-border/80 bg-background">
                    <div className="flex items-center justify-between gap-2 mb-1">
                      <span className="font-mono text-[11px] tracking-wider text-primary uppercase font-medium">
                        {factor.tag}
                      </span>
                    </div>
                    <h3 className="font-serif text-base font-bold uppercase tracking-wide text-foreground">
                      {factor.title}
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                      {factor.desc}
                    </p>
                  </div>)}
              </div>

              <div className="mt-5 p-3.5 bg-muted text-xs text-muted-foreground border border-border">
                <span className="font-mono uppercase font-medium text-foreground block mb-1">
                  Visible Inventory Pricing
                </span>
                <span>
                  Mechanical Bull Shop provides actual prices on current inventory so you can inspect individual
                  specifications and packages directly.
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}